import Hammer from '@egjs/hammerjs';
import { Center, Gesture, GestureState, Direction, PanGesture, PressGesture, PinchPanRotateGesture, TapGesture } from './types';

function getCenter(ps: PointerEvent[]): Center {
  const f = (1.0 / ps.length) * window.devicePixelRatio;
  return ps.reduce(
    (acc, p) => {
      return {
        relativeCenter: { x: acc.relativeCenter.x + p.offsetX * f, y: acc.relativeCenter.x + p.offsetX * f },
        viewportCenter: { x: acc.viewportCenter.x + p.clientX * f, y: acc.viewportCenter.y + p.offsetY * f },
      };
    },
    {
      relativeCenter: { x: 0, y: 0 },
      viewportCenter: { x: 0, y: 0 },
    }
  );
}

/**
 * TODO check if pointers stay in order, otherwise sort the array, because the pointers array is rather a set than an
 * array
 * returns a computed hash from all pointerEvents
 */
function getGestureId(ps: PointerEvent[]) {
  return ps.reduce((id, p, i) => id + p.pointerId * i * 214748363, 0);
}

/**
 *
 * this should be e.pointers, but hammerjs is kind of broken with the new unified PointerEvent,
 * this could be a potential bug source, as e.changedPointers are only the changed pointers since the last event,
 * but all pointers are needed, we should not rely too much on this hammerjs adapter...
 *
 * this function tries to fix issues with hammerjs pointers, but this doesn't seem to work, anyway, this is the best solution that
 * came into my mind...
 */
function getPointers(e: HammerInput): PointerEvent[] {
  return Array.from(new Set([...e.changedPointers, ...e.pointers]));
}

/**
 * creates the gesture base type, if the pointer events are in correct format, otherwise returns undefined:
 *   - there is at least one pointer
 *   - all pointers have the same pointerType
 */
function createGesture(name: string, ps: PointerEvent[]): Gesture | undefined {
  const l = ps.length;
  if (l > 0) {
    const t = ps[0].pointerType as 'touch' | 'mouse' | 'pen';
    // ensure that only one type of pointer is used
    // TODO in the future (library) support multiple gestures of different pointer types at the same time
    // for e.g. tablets that have a pen, while supporting pinch to zoom or something like that
    if (ps.filter((p) => p.pointerType === t).length === l) {
      return {
        id: getGestureId(ps),
        name,
        pointerType: t,
        pointerCount: l,
        timestamp: Date.now(),
      };
    }
  }
  return undefined;
}

function createPanGesture(name: string, e: HammerInput, le: HammerInput | undefined): PanGesture | undefined {
  const ps = getPointers(e);
  const gesture = createGesture(name, ps);
  const vX = e.velocityX;
  const vY = e.velocityY;
  let state: GestureState;
  // hacky logic adapter to achieve the states defined in `types`
  if (le) {
    const lps = getPointers(le);
    if ((le.type === 'pan' && e.type === 'pinch') || (le.type === 'pinch' && e.type === 'pan' && e.eventType === 2)) state = GestureState.BEGIN;
    else if (le.type === 'pinch' && e.type === 'pan' && e.eventType === 4) return undefined;
    else state = ps.length !== lps.length ? GestureState.END : e.eventType;
  } else {
    state = GestureState.BEGIN;
  }
  if (gesture) {
    const panGesture: PanGesture = {
      ...gesture,
      ...getCenter(ps),
      state,
      panDelta: { x: e.deltaX, y: e.deltaY, total: e.distance },
      panVelocity: { x: vX, y: vY, total: Math.sqrt(vX * vX + vY * vY) },
      panDirection: e.direction as Direction,
    };
    return panGesture;
  }
  return undefined;
}

/**
 * An opinionated Adapter for the internal touch API, with some good defaults
 */
export default class HammerJsAdapter {
  private _element: HTMLElement | SVGElement;

  private _hm: HammerManager;

  private _lastEvent?: HammerInput;

  constructor(element: HTMLElement | SVGElement) {
    this._element = element;
    this._hm = new Hammer.Manager(element);

    ////////////////////////////////////////////////////////// register gestures

    this._hm.add(new Hammer.Pan({ pointers: 0 }));
    this._hm.add(new Hammer.Pinch()).recognizeWith(this._hm.get('pan'));
    this._hm.add(new Hammer.Press());
    this._hm.add(new Hammer.Tap({ event: 'doubletap', taps: 2 }));
    this._hm.add(new Hammer.Tap({ event: 'singletap' })).requireFailure(this._hm.get('doubletap'));

    const emitTap = (name: string, taps: number) => (e: HammerInput) => {
      const ps = getPointers(e);
      const gesture = createGesture(name, ps);
      if (gesture) {
        const tap: TapGesture = {
          ...gesture,
          ...getCenter(ps),
          tapCount: taps,
        };
        this.emitGestureEvent(name, tap);
        this._lastEvent = e;
      }
    };

    const emitPan = (e: HammerInput) => {
      const name = 'pan';
      const gesture = createPanGesture(name, e, this._lastEvent);
      // pointerCount 2 is handled in pinch-pan-rotate
      if (gesture && gesture.pointerCount === 1) {
        this.emitGestureEvent(name, gesture);
        this._lastEvent = e;
      }
    };

    const emitPinchPanRotate = (e: HammerInput) => {
      const name = 'pinch-pan-rotate';
      const panGesture = createPanGesture(name, e, this._lastEvent);
      const ps = getPointers(e);
      if (panGesture && ps.length === 2) {
        const diffX = ps[0].clientX - ps[1].clientX;
        const diffY = ps[0].clientY - ps[1].clientY;
        const pinchDistance = Math.sqrt(diffX * diffX + diffY * diffY);
        const pinchPanRotateGesture: PinchPanRotateGesture = {
          ...panGesture,
          pinchScale: e.scale,
          pinchVelocity: 1, // TODO calculate this, but not yet needed (fling would be a use case)
          pinchDistance,
          rotateDeltaAngle: e.angle,
          rotateAbsoluteAngle: 0, // TODO
          rotateAngleVelocity: 0, // TODO
        };
        this.emitGestureEvent(name, pinchPanRotateGesture);
        this._lastEvent = e;
      }
    };

    const emitPress = (e: HammerInput) => {
      const name = 'press';
      const ps = getPointers(e);
      const gesture = createGesture(name, ps);
      if (gesture) {
        const pressGesture: PressGesture = {
          ...gesture,
          ...getCenter(ps),
          state: e.eventType,
        };
        this.emitGestureEvent(name, pressGesture);
        this._lastEvent = e;
      }
    };

    this._hm.on('pinch', emitPinchPanRotate);
    this._hm.on('pan', emitPan);
    this._hm.on('press', emitPress);
    this._hm.on('pressup', emitPress);
    this._hm.on('singletap', emitTap('singletap', 1));
    this._hm.on('doubletap', emitTap('doubletap', 2));
  }

  emitGestureEvent(name: string, gesture: PanGesture | PressGesture | PinchPanRotateGesture | TapGesture) {
    this._element.dispatchEvent(
      new CustomEvent(name, {
        detail: gesture,
      })
    );
  }
}
